/**
 * MessageRow — single message bubble with role-based alignment, status badge,
 * and Framer Motion enter animation tied to the active theme's `bubbleIn`
 * spec.
 *
 * The row reads the active theme manifest from `useThemeStore` so the bubble
 * entry duration follows the theme (Requirement 7.3) and reduced-motion is
 * honored (the `useThemeStore` slice serves a `derivedSpecs`-shortened variant
 * when the End_User opts out of motion). Color is never the sole indicator of
 * status — the embedded `<StatusBadge>` always renders both an icon and a
 * visible localized label (Requirement 10.7).
 *
 * Validates: Requirements 7.1, 7.7, 10.1, 10.4, 10.7.
 */

import type { ReactElement } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import type { Message } from '../../domain/types';
import { useThemeStore } from '../../state';
import { StatusBadge } from '../common';

import styles from './MessageRow.module.css';

interface MessageRowProps {
  message: Message;
  /** Called when the user taps "try again" on a failed assistant message. */
  onRetry?: (messageId: string) => void;
}

export function MessageRow({ message, onRetry }: MessageRowProps): ReactElement {
  const { t } = useTranslation();
  const theme = useThemeStore((s) => s.current);
  const isUser = message.role === 'user';
  const durationSec = theme.animation.bubbleIn.duration / 1000;

  const canRetry =
    !isUser &&
    (message.status === 'failed' || message.status === 'softFallback') &&
    onRetry !== undefined;

  const rowClass = [
    styles.row,
    isUser ? styles.rowUser : styles.rowAssistant,
  ].join(' ');

  return (
    <motion.div
      className={rowClass}
      initial={{ opacity: 0, y: 8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: durationSec, ease: [0.2, 0.8, 0.2, 1] }}
      role="article"
      aria-label={isUser ? 'Your message' : 'AI message'}
      data-role={message.role}
    >
      <div
        className={[
          styles.bubble,
          isUser ? styles.bubbleUser : styles.bubbleAssistant,
        ].join(' ')}
      >
        {/* Attached image — shown above the text content */}
        {isUser && message.imageData != null && (
          <img
            src={`data:${message.imageData.mimeType};base64,${message.imageData.base64}`}
            alt={message.imageData.fileName}
            className={styles.attachedImage}
          />
        )}
        {message.content.length > 0 && (
          <p className={styles.content}>{message.content}</p>
        )}
        <StatusBadge status={message.status} className={styles.status ?? ''} />
        {canRetry && (
          <button
            type="button"
            className={styles.retryBtn}
            onClick={() => onRetry(message.id)}
          >
            {t('chat.tryAgain')}
          </button>
        )}
      </div>
    </motion.div>
  );
}
